// The planet's cloud, from the world painter's tiles (observer-centric LOD, step 7; design: claude/world-painter-design.md). One full-screen pass, drawn after the scene's transparent things
// (the sea at order 2, the cloud volume's composite at 10), over the sky pass and everything under it.
//
// Why a pass of its own and not a part of the far shell's planet: the sea is drawn after the shell and is opaque to the horizon, so a cloud painted into the shell is hidden by it for as long as
// the sea is whole. This pass is added (screen blend) over what is there, with the cloud's weight at each pixel set by hand-over.ts: it draws where the near field's cloud and sea do not.
//
// What it draws, for each pixel, from the eye's ray (all in doubles on the CPU, in floats here relative to the planet's centre):
//   * three decks, the high one first: the ray meets a sphere at the deck's height (a first guess, then the height the tiles give there: parallax is right at the limb), and the tiles are
//     read at that point: the global tier everywhere and the nadir windows over it, each window fading into the next coarser over its outer tenth;
//   * below the tile's cell the cloud is the tile's cover as a threshold of a fractal field (Gaussian closure, as the painter's own: worldpaint._probit_cover): a pixel is cloud where
//     Phi((probit(cover) + G) / sqrt(1 - V)), G the octaves of unit-variance noise from the cell's size down to what the pixel resolves, V their variance, so the mean over a cell is the
//     tile's cover at every distance and the edge sharpens exactly as fast as the pixel can show it. The noise is position keyed (the planet's own frame), so it never moves with the eye;
//   * the deck's optical depth from its water (kappa times the in-cloud path), its albedo from that (two-stream), its top's relief from the noise's gradient times the deck's depth (the lit
//     and the shaded side of every lobe, with the sun), the sun's and the view's air through the same tables the sky pass uses;
//   * decks stack front to back: what a deck lets through is what the ones below show, and the sun that reaches a deck is the cell-mean cover of the ones above (no cast shadows yet).
// Units are the sky's, through the same display curve as the far shell: 1 - exp(-eGain * U).
import * as THREE from 'three';
import type { FarBody } from './bodies';
import type { FarShellFrame } from './farShell';
import { AIR_COLUMN_GLSL, FAR_VERT } from './farShell';
import { HANDOVER_GLSL, handover } from './handover';
import { worldTiles, tileUniforms, TILES_GLSL } from './worldTiles';

/** Strength of the lobes' relief (the surface normal is tilted by this times the height field's slope), and the least and greatest slope it may reach. */
const RELIEF = 1.6;
const SLOPE_MAX = 1.6;

/** Unit-variance single-octave noise: the std of a raw sample of `gnoiseD`'s value (measured: t/t_noise.mjs). */
export const NOISE_STD = 0.1915;
/** The fractal's first wavelength (m) and how many octaves it has (down to 64 m). */
export const LREF = 65536;
export const NOCT = 11;
/** An octave is shown from a wavelength of FOOT_K pixels (gone below it) to twice that (in full). */
export const FOOT_K = 4;
/** The surface's slope (the shading) comes only from octaves of at least RELIEF_K pixels per wavelength (in full from twice that): the finest octaves roughen the cloud's edge, not its shading. */
export const RELIEF_K = 16;

/**
 * Gradient noise with its gradient, on integer lattice hashes (PCG3D: the same bits on every GPU, and no precision loss with large coordinates as a `sin` hash has). `gnoiseD(x, seed)` is
 * (value, d value / d x) at the lattice position x; the value has std NOISE_STD and the lattice cell is one unit.
 */
export const GNOISE_GLSL = /* glsl */ `
uvec3 pcg3(uvec3 v) {
  v = v * 1664525u + 1013904223u;
  v.x += v.y * v.z; v.y += v.z * v.x; v.z += v.x * v.y;
  v ^= v >> 16u;
  v.x += v.y * v.z; v.y += v.z * v.x; v.z += v.x * v.y;
  return v;
}
vec3 latticeGrad(ivec3 p, int seed) {
  uvec3 h = pcg3(uvec3(p + ivec3(seed * 7919, seed * 104729 + 13, seed * 1299709 + 29)));
  return vec3(h >> 8u) * (1.0 / 8388608.0) - 1.0;
}
vec4 gnoiseD(vec3 x, int seed) {
  ivec3 i = ivec3(floor(x));
  vec3 f = x - vec3(i);
  vec3 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  vec3 du = 30.0 * f * f * (f * (f - 2.0) + 1.0);
  vec3 ga = latticeGrad(i, seed), gb = latticeGrad(i + ivec3(1, 0, 0), seed), gc = latticeGrad(i + ivec3(0, 1, 0), seed), gd = latticeGrad(i + ivec3(1, 1, 0), seed);
  vec3 ge = latticeGrad(i + ivec3(0, 0, 1), seed), gf = latticeGrad(i + ivec3(1, 0, 1), seed), gg = latticeGrad(i + ivec3(0, 1, 1), seed), gh = latticeGrad(i + ivec3(1, 1, 1), seed);
  float va = dot(ga, f), vb = dot(gb, f - vec3(1, 0, 0)), vc = dot(gc, f - vec3(0, 1, 0)), vd = dot(gd, f - vec3(1, 1, 0));
  float ve = dot(ge, f - vec3(0, 0, 1)), vf = dot(gf, f - vec3(1, 0, 1)), vg = dot(gg, f - vec3(0, 1, 1)), vh = dot(gh, f - vec3(1, 1, 1));
  float k1 = vb - va, k2 = vc - va, k3 = ve - va, k4 = va - vb - vc + vd, k5 = va - vc - ve + vg, k6 = va - vb - ve + vf, k7 = -va + vb + vc - vd + ve - vf - vg + vh;
  vec3 g1 = gb - ga, g2 = gc - ga, g3 = ge - ga, g4 = ga - gb - gc + gd, g5 = ga - gc - ge + gg, g6 = ga - gb - ge + gf, g7 = -ga + gb + gc - gd + ge - gf - gg + gh;
  float v = va + u.x * k1 + u.y * k2 + u.z * k3 + u.x * u.y * k4 + u.y * u.z * k5 + u.z * u.x * k6 + u.x * u.y * u.z * k7;
  vec3 dv = ga + u.x * g1 + u.y * g2 + u.z * g3 + u.x * u.y * g4 + u.y * u.z * g5 + u.z * u.x * g6 + u.x * u.y * u.z * g7
          + du * vec3(k1 + k4 * u.y + k6 * u.z + k7 * u.y * u.z, k2 + k4 * u.x + k5 * u.z + k7 * u.z * u.x, k3 + k5 * u.y + k6 * u.x + k7 * u.x * u.y);
  return vec4(v, dv);
}
`;

const FRAG = /* glsl */ `
precision highp float;
precision highp sampler2DArray;
uniform vec4  uC;                // the planet: centre from the eye (m, render axes), radius
uniform vec3  uLd;               // toward the star (unit)
uniform vec3  uFl;               // the star's flux per channel at the planet
uniform vec3  uFx[3];            // the patch's frame (east, north, up) in render axes: a direction's coordinates in it are what the tiles' body frame is turned from
uniform vec4  uAir;              // zenith optical depth of the clean air per channel (rgb), aerosol (w)
uniform float uOn;               // 0 when nothing is to be drawn (no tiles, or a render into a probe)
uniform float uEGain, uSrgbOut, uPixAng;
uniform int   uDbg;
${HANDOVER_GLSL}
varying vec3 vDir;
const float PI_ = 3.14159265358979;
${AIR_COLUMN_GLSL}
${TILES_GLSL}
${GNOISE_GLSL}

const float REL = ${RELIEF.toFixed(2)}, SLOPE_MAX = ${SLOPE_MAX.toFixed(2)};
const float INV_STD = ${(1 / NOISE_STD).toFixed(5)};
const float NOISE_STD = ${NOISE_STD.toFixed(5)};
const float LREF = ${LREF.toFixed(1)};
const float FOOT_K = ${FOOT_K.toFixed(1)};
const float RELIEF_K = ${RELIEF_K.toFixed(1)};
const int NOCT = ${NOCT};
const mat3 ROT = mat3(0.00, 0.80, 0.60, -0.80, 0.36, -0.48, -0.60, -0.48, 0.64);
const float H0[3] = float[3](1600.0, 3600.0, 9000.0);              // a deck's height for the first guess of where the ray meets it (m)

vec3 encodeSrgb(vec3 c) { return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c)); }

// the Gaussian closure: the normal's quantile and distribution (Abramowitz and Stegun 26.2.23; the tanh form of the distribution), and the density
float probit(float p) {
  float q = min(p, 1.0 - p);
  float t = sqrt(-2.0 * log(q));
  float x = t - (2.515517 + 0.802853 * t + 0.010328 * t * t) / (1.0 + 1.432788 * t + 0.189269 * t * t + 0.001308 * t * t * t);
  return p < 0.5 ? -x : x;
}
float Phi(float x) { return 0.5 + 0.5 * tanh(0.7978845608 * (x + 0.044715 * x * x * x)); }
float phiPdf(float x) { return 0.3989423 * exp(-0.5 * x * x); }

// ── a deck at a pixel ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// The nearest meeting of the ray (from o, relative to the planet's centre) with the sphere of radius Rk, for an eye outside it; -1 if none.
float hitShell(vec3 o, vec3 d, float Rk) {
  if (dot(o, o) <= Rk * Rk) return -1.0;
  float b = dot(o, d);
  float perp = length(cross(o, d));
  if (perp >= Rk) return -1.0;
  float s2 = (Rk - perp) * (Rk + perp);
  float t = -b - sqrt(s2);
  return t > 0.0 ? t : -1.0;
}

// The deck's tile values where the ray meets the deck (the height the tiles give there), the distance t, the unit normal of the planet there, the patch frame's coordinates q, the log of the cell.
bool deckHit(int k, vec3 o, vec3 d, float R, out vec4 v, out float t, out vec3 nrm, out vec3 q, out float lnDx) {
  float t1 = hitShell(o, d, R + H0[k]);
  if (t1 < 0.0) return false;
  vec3 n1 = normalize(o + d * t1);
  vec3 q1 = vec3(dot(uFx[0], n1), dot(uFx[1], n1), dot(uFx[2], n1));
  float lod1 = max(log2(uPixAng * t1 * 2.0 / uTP.z), 0.0);
  vec4 v1 = fieldAt(toBody(q1), k, lod1, lnDx);
  float top1 = k == 0 ? v1.w : v1.z + v1.w;
  float alt = clamp(v1.z + 0.75 * (top1 - v1.z), 100.0, 20000.0);
  float t2 = hitShell(o, d, R + alt);
  if (t2 < 0.0) { t2 = t1; nrm = n1; q = q1; v = v1; t = t1; return true; }
  nrm = normalize(o + d * t2);
  q = vec3(dot(uFx[0], nrm), dot(uFx[1], nrm), dot(uFx[2], nrm));
  float lod2 = max(log2(uPixAng * t2 * 2.0 / uTP.z), 0.0);
  v = fieldAt(toBody(q), k, lod2, lnDx);
  t = t2;
  return true;
}

// The fractal below the tile's cell: octaves of unit-variance noise at fixed wavelengths (LREF / 2^j: the same lattice for every tile and pixel, so the field does not change where one tier gives way to
// the next, only which octaves the tile's own cells already hold), each 0.406 of the variance of the last (H = 0.65, the painter's exponent). An octave comes in as the cell size passes its wavelength
// (one octave of fade, wc) and as the pixel can show it (wavelength past FOOT_K footprints, wo). The variances are scaled to 1 over the octaves the cell leaves out, so V is the share of them
// the pixel shows (of 1: the rest is below the pixel). Returns the sum G and its gradient per metre across the surface (render axes).
float subCell(vec3 q, vec3 nrm, float dx, float foot, int seed, out vec3 gsl, out float vr) {
  float S = 0.0;
  for (int j = 0; j < NOCT; j++) {
    float lam = LREF * exp2(-float(j));
    float wc = clamp(log2(dx / lam) + 1.0, 0.0, 1.0);
    S += wc * wc * pow(0.406, float(j));
  }
  float G = 0.0, V = 0.0;
  vec3 gq = vec3(0.0);
  mat3 Rc = mat3(1.0);
  for (int j = 0; j < NOCT; j++) {
    float lam = LREF * exp2(-float(j));
    float wc = clamp(log2(dx / lam) + 1.0, 0.0, 1.0);
    float wo = clamp(lam / (FOOT_K * foot) - 1.0, 0.0, 1.0);
    if (wo <= 0.0) break;
    if (wc > 0.0) {
      float a = sqrt(pow(0.406, float(j)) / S) * wc * wo;
      vec4 nd = gnoiseD(Rc * q * (uTP.x / lam), seed + j);
      G += a * nd.x * INV_STD;
      gq += a * INV_STD * clamp(lam / (RELIEF_K * foot) - 1.0, 0.0, 1.0) * (nd.yzw * Rc) / lam;
      V += a * a;
    }
    Rc = ROT * Rc;
  }
  vec3 g = uFx[0] * gq.x + uFx[1] * gq.y + uFx[2] * gq.z;
  gsl = g - dot(g, nrm) * nrm;
  vr = V;
  return G;
}

void main() {
  if (uOn < 0.5) discard;
  vec3 d = normalize(vDir);
  vec3 o = -uC.xyz;
  float R = uC.w;
  vec3 L = uLd, F = uFl;
  vec3 U = vec3(0.0);
  float trans = 1.0, sunT = 1.0;
  vec2 cEye = column(o, -d, R, 6);
  float dbg = 0.0;
  for (int kk = 0; kk < 3; kk++) {
    int k = 2 - kk;
    vec4 v; float t, lnDx; vec3 nrm, q;
    if (!deckHit(k, o, d, R, v, t, nrm, q, lnDx)) continue;
    float cov = clamp(v.x, 0.0, 1.0);
    // the hand-over: the shell has the cloud where the near field has given way, by this pixel's range and its distance from the patch's centre
    float sq = length(q.xy);
    vec2 en = sq > 1e-9 ? q.xy / sq * (atan(sq, q.z) * R) : vec2(0.0);
    float w = 1.0 - uHand.w * handRange(t) * handPatch(en);
    if (cov < 0.002 || w < 0.002) continue;
    float base = v.z;
    float top = k == 0 ? v.w : v.z + v.w;
    float depth = max(top - base, 150.0);
    // the structure below the cell: a threshold of the fractal, sharp as the pixel shows it
    float foot = uPixAng * t / max(dot(nrm, -d), 0.1);
    vec3 gsl; float vr;
    float G = subCell(q, nrm, exp(lnDx), foot, 31 * k + 5, gsl, vr);
    float mu = max(1.0 - vr, 0.001);
    float pc = clamp(cov, 0.002, 0.998);
    float arg = (probit(pc) + G) / sqrt(mu);
    float mask = Phi(arg);
    float dphi = phiPdf(arg) / sqrt(mu);
    vec3 slope = gsl * (REL * depth * (dphi + 0.35));
    float sl = length(slope);
    slope *= min(1.0, SLOPE_MAX / max(sl, 1e-6));
    vec3 nt = normalize(nrm - slope);
    float cosL = dot(nrm, L);
    // what this cloud does to light, from the world's cloud tables: its reflectance of the sun's beam at this sun angle (a colour), and of the sky's light (how opaque it is to the eye)
    vec4 resp = deckResp(k, cov, v.y, cosL);
    float lit = max(cosL * 0.9 + 0.1 * smoothstep(-0.3, 0.0, cosL), 0.0);
    float relief = clamp(dot(nt, L) / max(cosL, 0.12), 0.25, 2.2);
    // the air this cloud is seen through: the sun's way down to it and the eye's way up to it, each from the deck's own height (the sky table's transmittance would take the air below the cloud too)
    vec3 pk = nrm * (R + 0.75 * top);
    vec2 cs = column(pk, L, R, 6) + max(column(pk, -d, R, 6) - cEye, vec2(0.0));
    vec3 Tk = exp(-(uAir.rgb * cs.x + vec3(uAir.w) * cs.y));
    float a = mask * resp.a * w;
    U += trans * sunT * (mask * w) * resp.rgb * lit * mix(1.0, relief, step(0.0, cosL)) * F * Tk;
    trans *= 1.0 - a;
    sunT *= 1.0 - cov * dot(resp.rgb, vec3(0.2126, 0.7152, 0.0722));
    dbg = k == 0 ? mask : dbg;
  }
  if (uDbg == 1) { gl_FragColor = vec4(vec3(dbg), 1.0); return; }
  if (trans > 0.9995) discard;
  vec3 acc = 1.0 - exp(-uEGain * U);
  if (uSrgbOut > 0.5) acc = encodeSrgb(acc);
  gl_FragColor = vec4(acc, 1.0);
}
`;

export class WorldCloud {
  readonly mesh: THREE.Mesh;
  private geo: THREE.BufferGeometry;
  private mat: THREE.ShaderMaterial;
  private fx: THREE.Vector3[] = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
  private srgb = true;
  private on = false;

  constructor() {
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    this.mat = new THREE.ShaderMaterial({
      vertexShader: FAR_VERT, fragmentShader: FRAG,
      transparent: true, depthTest: false, depthWrite: false, toneMapped: false, fog: false,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcColorFactor,
      blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor,
      uniforms: {
        uInvProj: { value: new THREE.Matrix4() },
        uC: { value: new THREE.Vector4() }, uLd: { value: new THREE.Vector3(0, 1, 0) }, uFl: { value: new THREE.Vector3() }, uFx: { value: this.fx },
        uAir: { value: new THREE.Vector4(0.05, 0.1, 0.22, 0.03) }, uOn: { value: 0 },
        uEGain: { value: 6 }, uSrgbOut: { value: 1 }, uPixAng: { value: 0.001 }, uDbg: { value: 0 },
        uHand: handover.hand, uHandP: handover.handP,
        ...tileUniforms(),
      },
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 11;                         // after the sea (2) and the cloud volume's composite (10), which it is the complement of
    this.mesh.visible = false;
    this.mesh.name = 'world cloud (world painter tiles)';
    this.mesh.onBeforeRender = (r, _s, camera) => {
      const u = this.mat.uniforms;
      (u.uInvProj!.value as THREE.Matrix4).copy(camera.projectionMatrixInverse);
      const toTarget = r.getRenderTarget() !== null;
      u.uSrgbOut!.value = toTarget ? 0 : (this.srgb ? 1 : 0);
      u.uOn!.value = this.on && !toTarget ? 1 : 0;                        // (a probe or a shadow pass is not a picture of the planet from here)
    };
  }

  /** Per frame. `home`: the home planet as the registry gives it (render axes, from the eye); `f`: the far shell's frame (air, exposure, the sky table); `pixAng`: the angle one pixel spans (rad). */
  update(f: FarShellFrame | null, home: FarBody | null, pixAng: number): void {
    const T = worldTiles;
    const on = !!f && !!home && T.haveGlobal && handover.avail > 0;
    this.on = on;
    this.mesh.visible = on;
    if (!on || !f || !home) return;
    const u = this.mat.uniforms;
    (u.uC!.value as THREE.Vector4).set(home.c[0], home.c[1], home.c[2], home.r);
    (u.uLd!.value as THREE.Vector3).set(home.lightDir[0], home.lightDir[1], home.lightDir[2]);
    (u.uFl!.value as THREE.Vector3).set(home.flux[0], home.flux[1], home.flux[2]);
    for (let k = 0; k < 3; k++) this.fx[k]!.set(home.fixed[3 * k]!, home.fixed[3 * k + 1]!, home.fixed[3 * k + 2]!);
    (u.uAir!.value as THREE.Vector4).set(f.air[0], f.air[1], f.air[2], f.air[3]);
    u.uEGain!.value = f.eGain; this.srgb = f.srgbOut; u.uSrgbOut!.value = f.srgbOut ? 1 : 0; u.uPixAng!.value = pixAng;
    T.setUniforms(u);
  }

  /** A test switch: 1 shows the low deck's mask. */
  setDebug(n: number): void { this.mat.uniforms.uDbg!.value = n; }

  dispose(): void { this.geo.dispose(); this.mat.dispose(); }
}

